import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Breadcrumbs from '../components/Breadcrumbs';
import { FaPhone, FaMapMarkerAlt, FaHome, FaPrayingHands } from 'react-icons/fa';
import { getFileUrl } from '../utils/fileUtils';
import api from '../services/api';
import './PastoralDetalle.css';

// Convierte URL de YouTube a embed
function youtubeEmbedUrl(url) {
  if (!url || typeof url !== 'string') return null;
  const u = url.trim();
  if (!u) return null;
  const match = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  return match ? `https://www.youtube.com/embed/${match[1]}` : null;
}

const PastoralDetalle = () => {
  const { clave } = useParams();
  const [imagenPrincipal, setImagenPrincipal] = useState(0);

  const claveNorm = (clave || '').replace(/-/g, '_');

  const { data: seccion, isLoading, error } = useQuery({
    queryKey: ['seccion-pastoral', claveNorm],
    queryFn: async () => {
      const res = await api.get(`/secciones-pastoral/${claveNorm}`);
      return res.data;
    },
    enabled: !!claveNorm,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="pastoral-detalle-page">
        <Breadcrumbs />
        <div className="container"><p className="loading">Cargando...</p></div>
      </div>
    );
  }
  if (error || !seccion) {
    return (
      <div className="pastoral-detalle-page">
        <Breadcrumbs />
        <div className="container">
          <section className="section">
            <h1 className="page-title">Sección no encontrada</h1>
            <p>No existe una sección pastoral con esta dirección.</p>
            <Link to="/pastoral" className="pastoral-detalle-back-link">
              <FaHome /> Volver a Pastoral
            </Link>
          </section>
        </div>
      </div>
    );
  }

  const unaSolaFoto = seccion.una_sola_foto === 1 || (seccion.imagenes && seccion.imagenes.length <= 1);
  const imagenes = (seccion.imagenes || []).map((i) => (typeof i === 'string' ? i : i?.imagen_url || i));
  const placeholder = '/images/placeholder-casa.svg';
  const embedUrl = youtubeEmbedUrl(seccion.video_url);

  return (
    <div className="pastoral-detalle-page">
      <Breadcrumbs />
      <section className="section pastoral-detalle-hero">
        <div className="container">
          <h1 className="page-title"><FaPrayingHands aria-hidden /> {seccion.titulo}</h1>
        </div>
      </section>

      <section className="section pastoral-detalle-content">
        <div className="container">
          <div className="pastoral-detalle-grid">
            <div className="pastoral-detalle-imagenes">
              {imagenes.length > 0 ? (
                unaSolaFoto ? (
                  <div className="pastoral-detalle-imagen-unica">
                    <img
                      src={getFileUrl(imagenes[0]) || placeholder}
                      alt={seccion.titulo}
                      onError={(e) => { e.target.src = placeholder; }}
                    />
                  </div>
                ) : (
                  <>
                    <div className="pastoral-detalle-imagen-principal">
                      <img
                        src={getFileUrl(imagenes[imagenPrincipal] || imagenes[0]) || placeholder}
                        alt={`${seccion.titulo} - ${imagenPrincipal + 1}`}
                        onError={(e) => { e.target.src = placeholder; }}
                      />
                    </div>
                    {imagenes.length > 1 && (
                      <div className="pastoral-detalle-galeria-mini">
                        {imagenes.map((url, idx) => (
                          <button
                            key={idx}
                            type="button"
                            className={`miniatura ${imagenPrincipal === idx ? 'active' : ''}`}
                            onClick={() => setImagenPrincipal(idx)}
                            aria-label={`Ver imagen ${idx + 1}`}
                          >
                            <img src={getFileUrl(url) || placeholder} alt="" onError={(e) => { e.target.src = placeholder; }} />
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )
              ) : seccion.imagen_url ? (
                <div className="pastoral-detalle-imagen-unica">
                  <img
                    src={getFileUrl(seccion.imagen_url) || placeholder}
                    alt={seccion.titulo}
                    onError={(e) => { e.target.src = placeholder; }}
                  />
                </div>
              ) : null}
            </div>
            <div className="pastoral-detalle-info">
              {seccion.texto && (
                <div className="pastoral-detalle-texto" dangerouslySetInnerHTML={{ __html: seccion.texto }} />
              )}
              {embedUrl && (
                <div className="pastoral-detalle-video">
                  <iframe
                    title={`Video ${seccion.titulo}`}
                    src={embedUrl}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              )}
              {(seccion.telefono || seccion.direccion) && (
                <div className="pastoral-detalle-contacto">
                  {seccion.telefono && (
                    <p className="pastoral-detalle-contacto-item">
                      <FaPhone aria-hidden />
                      <a href={`tel:${seccion.telefono.replace(/\D/g, '')}`}>{seccion.telefono}</a>
                    </p>
                  )}
                  {seccion.direccion && (
                    <p className="pastoral-detalle-contacto-item">
                      <FaMapMarkerAlt aria-hidden />
                      <span>{seccion.direccion}</span>
                    </p>
                  )}
                </div>
              )}
              {!seccion.texto && !seccion.telefono && !seccion.direccion && !embedUrl && (
                <p className="pastoral-detalle-sin-datos">Contenido disponible próximamente.</p>
              )}
              <Link to="/pastoral" className="pastoral-detalle-back-link">
                ← Volver a Pastoral
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PastoralDetalle;
